import { CanvasElement, AspectRatio, GlobalSettings, PromptFormat } from './engineTypes';
import { formatPrompt } from './outputStyleFormatter';
import { calculateSceneDynamics, SceneDynamics } from './sceneDynamics';
import { validatePrompt, ValidationResult } from '@/logic/validatePrompt';
import { getStylePrompt } from '@/data/options/styleOptions';
import { PALETTE_PRESETS, CustomPalette, palettePromptFromCustom, palettePromptWithCodes } from '@/data/options/colorPalettes';
import { getLightingPrompt } from '@/data/options/lightingOptions';
import { detectConflicts, resolveConflicts, isGenerationLocked, Conflict } from './conflictEngine';
import { inferCinematicLayers, InferredLayer } from './autoStructure';
import { buildVFXLayers, describeVFXLayers, VFXLayer, getVFXIntensity, VFXIntensity } from './vfxLayerEngine';
import { updateStyleMemory, getStyleTendencies, getStyleMemoryEnrichments, StyleTendency } from './styleMemory';
import { sanitizeSceneDescription } from '@/engine/fixStrategyEngine';
import type { TokenGroup } from '@/engine/tokenRegistry';

/**
 * Determine which token groups are already covered by canvas elements,
 * so we can strip them from sceneDescription to prevent duplication.
 */
function getCoveredTokenGroups(elements: CanvasElement[]): TokenGroup[] {
  const groups: TokenGroup[] = [];
  const hasCamera = elements.some((el) => el.type === 'camera');
  const hasLight = elements.some((el) => el.type === 'light');
  
  if (hasCamera) {
    groups.push('camera_lens', 'camera_perspective', 'camera_shot_type', 'camera_body', 'camera_sensor', 'dof_mode');
  }
  if (hasLight) {
    groups.push('color_temperature');
  }
  return groups;
}

function describePosition(x: number, y: number): string {
  const h = x < 30 ? 'left' : x > 70 ? 'right' : 'center';
  const v = y < 30 ? 'upper' : y > 70 ? 'lower' : 'middle';
  if (h === 'center' && v === 'middle') return 'center';
  if (h === 'center') return v;
  if (v === 'middle') return h;
  return `${v}-${h}`;
}

function describeScale(scale: number, type: string): string {
  if (type === 'character') {
    if (scale >= 2.5) return 'filling the entire frame';
    if (scale >= 2) return 'prominently large in frame';
    if (scale >= 1.5) return 'significantly present';
    if (scale <= 0.6) return 'small and distant';
    return '';
  }
  if (type === 'light') {
    if (scale >= 2) return 'large area light source';
    if (scale <= 0.6) return 'pinpoint focused light';
    return '';
  }
  return '';
}

function sliderLevel(val: number | string | undefined, max = 100): string {
  const n = typeof val === 'string' ? parseInt(val) || 0 : (val ?? 0);
  const pct = n / max;
  if (pct <= 0) return '';
  if (pct <= 0.2) return 'subtle';
  if (pct <= 0.4) return 'light';
  if (pct <= 0.6) return 'moderate';
  if (pct <= 0.8) return 'strong';
  return 'intense';
}

/**
 * Remove duplicate phrases and near-duplicate tokens from the final prompt.
 * Works by splitting into comma/period segments and keeping only the first
 * occurrence of each normalized phrase.
 */
function deduplicatePrompt(text: string): string {
  // Split by sentence boundaries
  const sentences = text.split(/\.\s*/);
  const seen = new Set<string>();
  const result: string[] = [];

  for (const sentence of sentences) {
    if (!sentence.trim()) continue;
    // Split each sentence by commas and deduplicate segments
    const segments = sentence.split(/,\s*/);
    const uniqueSegments: string[] = [];

    for (const seg of segments) {
      const normalized = seg.trim().toLowerCase()
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ');
      if (!normalized) continue;
      if (seen.has(normalized)) continue;
      // Also check if this is a substring of something already seen or vice versa
      let isRedundant = false;
      for (const prev of seen) {
        if (prev.includes(normalized) || normalized.includes(prev)) {
          // Keep the longer one
          if (normalized.length <= prev.length) {
            isRedundant = true;
            break;
          } else {
            seen.delete(prev);
          }
        }
      }
      if (isRedundant) continue;
      seen.add(normalized);
      uniqueSegments.push(seg.trim());
    }

    if (uniqueSegments.length > 0) {
      result.push(uniqueSegments.join(', '));
    }
  }

  return result.join('. ').replace(/\.\s*\./g, '.').replace(/\s{2,}/g, ' ').trim();
}

function describeLighting(el: CanvasElement): string {
  const pos = describePosition(el.x, el.y);
  const p = el.properties;
  const parts: string[] = [];

  const intensityVal = parseInt(p.intensity || '50');
  // Use the dynamic option system for light style
  const lightDesc = getLightingPrompt(p.lightStyle || 'natural', intensityVal);
  if (lightDesc) {
    parts.push(`${lightDesc} from the ${pos}`);
  } else {
    const intensityWord = intensityVal <= 15 ? 'barely perceptible' : intensityVal <= 30 ? 'soft, diffused' : intensityVal <= 50 ? 'balanced' : intensityVal <= 75 ? 'strong, pronounced' : 'dramatic, blazing';
    parts.push(`${intensityWord} ${(p.lightStyle || 'natural').replace(/_/g, ' ')} lighting from the ${pos}`);
  }

  if (p.lightPreset && p.lightPreset !== 'none') parts.push(`${p.lightPreset.replace(/_/g, ' ')} mood`);
  if (p.colorTempValue) parts.push(`${p.colorTempValue}K color temperature`);
  else if (p.colorTemp && p.colorTemp !== 'neutral') parts.push(`${p.colorTemp.replace(/_/g, ' ')} color temperature`);
  if (p.lightModifier && p.lightModifier !== 'none') parts.push(`through ${p.lightModifier.replace(/_/g, ' ')}`);

  const shadowLvl = sliderLevel(p.shadowSoftness);
  if (shadowLvl) parts.push(`${shadowLvl} shadow softness`);
  if (p.lightDirection) parts.push(`direction ${p.lightDirection}°`);
  if (p.godRays === 'true') parts.push('with volumetric god rays');

  const fogLvl = sliderLevel(p.fogDensity);
  if (fogLvl) parts.push(`${fogLvl} atmospheric fog`);

  const scaleDesc = describeScale(el.scale, el.type);
  if (scaleDesc) parts.push(scaleDesc);

  return parts.join(', ');
}

function describeCamera(el: CanvasElement): string {
  const pos = describePosition(el.x, el.y);
  const p = el.properties;
  const parts: string[] = [];

  parts.push(`${(p.shotType || 'medium').replace(/_/g, ' ')} shot`);
  parts.push(`${(p.lens || '50mm')} lens`);
  if (p.cameraType && p.cameraType !== 'dslr') parts.push(`shot on ${p.cameraType.replace(/_/g, ' ')}`);
  parts.push(`${(p.perspective || 'eye_level').replace(/_/g, ' ')} angle`);
  parts.push(`camera ${pos} in frame`);
  parts.push(`${(p.dof || 'moderate').replace(/_/g, ' ')} depth of field`);

  if (p.focusMode && p.focusMode !== 'subject') parts.push(`${p.focusMode.replace(/_/g, ' ')} focus`);
  if (p.cameraMovement && p.cameraMovement !== 'static') parts.push(`${p.cameraMovement.replace(/_/g, ' ')} movement`);
  if (p.sensorSize && p.sensorSize !== 'full_frame') parts.push(`${p.sensorSize.replace(/_/g, ' ')} sensor`);
  if (p.shutterSpeed) parts.push(`${p.shutterSpeed} shutter`);
  if (p.isoControl && p.isoControl !== '100') parts.push(`ISO ${p.isoControl}`);

  const motionBlurLvl = sliderLevel(p.motionBlur);
  if (motionBlurLvl) parts.push(`${motionBlurLvl} motion blur`);
  const dofIntLvl = sliderLevel(p.dofIntensity);
  if (dofIntLvl) parts.push(`${dofIntLvl} bokeh intensity`);

  return parts.join(', ');
}

function describeCharacter(el: CanvasElement): string {
  const pos = describePosition(el.x, el.y);
  const p = el.properties;
  const parts: string[] = [];

  parts.push(p.subject || 'subject');
  parts.push(`positioned at ${pos}`);

  if (p.pose && p.pose !== 'standing') parts.push(`${p.pose.replace(/_/g, ' ')} pose`);
  if (p.expression && p.expression !== 'neutral') parts.push(`${p.expression.replace(/_/g, ' ')} expression`);
  if (p.characterFraming && p.characterFraming !== 'full') parts.push(`${p.characterFraming.replace(/_/g, ' ')} framing`);
  if (p.microExpression && p.microExpression !== 'none') parts.push(`with ${p.microExpression.replace(/_/g, ' ')}`);
  if (p.eyeDirection && p.eyeDirection !== 'camera') parts.push(`eyes ${p.eyeDirection.replace(/_/g, ' ')}`);
  if (p.bodyRotation && parseInt(p.bodyRotation) > 0) parts.push(`body rotated ${p.bodyRotation}°`);
  const gestureLvl = sliderLevel(p.gestureIntensity);
  if (gestureLvl && p.gestureIntensity !== '50') parts.push(`${gestureLvl} gesture intensity`);
  const scaleDesc = describeScale(el.scale, el.type);
  if (scaleDesc) parts.push(scaleDesc);

  return parts.join(', ');
}

function describeGlobalSettings(gs: GlobalSettings): string[] {
  const parts: string[] = [];

  if (gs.renderStyle !== 'photorealistic') parts.push(`${gs.renderStyle.replace(/_/g, ' ')} rendering`);
  else parts.push('photorealistic rendering');

  if (gs.textureDetail > 70) parts.push('highly detailed textures');
  if (gs.skinDetail > 60) parts.push('enhanced skin pore detail');
  if (gs.filmGrain) parts.push('organic film grain');
  if (gs.hdrControl > 60) parts.push('high dynamic range');
  if (gs.resolutionScale !== '4k') parts.push(`${gs.resolutionScale.toUpperCase()} resolution`);
  if (gs.dynamicRange === 'extreme') parts.push('extreme dynamic range');
  if (gs.realCameraMode) parts.push('real camera simulation with lens imperfections');
  if (gs.goldenRatio) parts.push('golden ratio composition');
  if (gs.symmetryMode) parts.push('symmetrical composition');
  if (gs.centeredCinematic) parts.push('centered cinematic framing');
  if (gs.framingPreset && gs.framingPreset !== 'none') parts.push(`${gs.framingPreset.replace(/_/g, ' ')} framing`);
  if (gs.negativeSpace > 60) parts.push('generous negative space');
  if (gs.horizonTilt !== 0) parts.push(`horizon tilted ${gs.horizonTilt}°`);

  // Color grading
  if (gs.lutPreset && gs.lutPreset !== 'none') parts.push(`${gs.lutPreset.replace(/_/g, ' ')} color grade`);
  if (gs.filmStock && gs.filmStock !== 'none') parts.push(`shot on ${gs.filmStock.replace(/_/g, ' ')} film stock`);
  if (gs.contrastCurve > 70) parts.push('high contrast');
  if (gs.saturationIntensity > 70) parts.push('rich saturated colors');
  else if (gs.saturationIntensity < 30) parts.push('desaturated muted tones');
  if (gs.tealOrangePreset) parts.push('cinematic teal and orange color grading');
  if (gs.vintageMode) parts.push('vintage retro film look with faded blacks');
  if (gs.splitToningHighlight && gs.splitToningHighlight !== 'none') parts.push(`${gs.splitToningHighlight.replace(/_/g, ' ')} highlight toning`);
  if (gs.splitToningShadow && gs.splitToningShadow !== 'none') parts.push(`${gs.splitToningShadow.replace(/_/g, ' ')} shadow toning`);

  // Texture & detail
  if (gs.microDetail > 70) parts.push('ultra micro-detail enhancement');
  if (gs.sharpness > 70) parts.push('razor sharp detail');
  if (gs.surfaceClarity > 70) parts.push('crystal clear surface clarity');
  if (gs.fabricTexture > 30) parts.push(`${sliderLevel(gs.fabricTexture)} fabric texture detail`);
  if (gs.highFrequency > 70) parts.push('high frequency fine detail');

  // Beauty
  if (gs.skinSmoothing > 30) parts.push(`${sliderLevel(gs.skinSmoothing)} skin smoothing`);
  if (gs.poreRefinement > 30) parts.push('refined pore detail');
  if (gs.blemishRemoval > 30) parts.push('flawless skin');
  if (gs.highlightGlow > 30) parts.push(`${sliderLevel(gs.highlightGlow)} luminous skin glow`);
  if (gs.freqSeparation) parts.push('frequency separation retouching');
  if (gs.beautyPreset && gs.beautyPreset !== 'none') parts.push(`${gs.beautyPreset.replace(/_/g, ' ')} beauty look`);
  if (gs.naturalFashionToggle === 'fashion') parts.push('fashion campaign beauty retouching');

  // VFX
  if (gs.fogHaze > 20) parts.push(`${sliderLevel(gs.fogHaze)} atmospheric fog`);
  if (gs.dustParticles > 20) parts.push(`${sliderLevel(gs.dustParticles)} floating dust particles`);
  if (gs.godRaysGlobal > 20) parts.push(`${sliderLevel(gs.godRaysGlobal)} volumetric god rays`);
  if (gs.bloomGlow > 20) parts.push(`${sliderLevel(gs.bloomGlow)} bloom glow effect`);
  if (gs.motionBlurGlobal > 20) parts.push(`${sliderLevel(gs.motionBlurGlobal)} motion blur`);
  if (gs.particles !== 'none') parts.push(`${gs.particles.replace(/_/g, ' ')} particles floating`);
  if (gs.atmosphericDensity > 20) parts.push(`${sliderLevel(gs.atmosphericDensity)} atmospheric haze`);
  if (gs.cinematicSmoke > 20) parts.push(`${sliderLevel(gs.cinematicSmoke)} cinematic smoke layers`);
  if (gs.motionTrails) parts.push('motion trail effects');
  if (gs.energyAura) parts.push('glowing energy aura');
  if (gs.lightStreaks) parts.push('anamorphic light streaks');
  if (gs.chromaticAberration > 20) parts.push(`${sliderLevel(gs.chromaticAberration)} chromatic aberration`);
  if (gs.glitchEffect) parts.push('digital glitch distortion');
  if (gs.explosionIntensity > 0) parts.push(`${sliderLevel(gs.explosionIntensity)} pyrotechnic effects`);
  if (gs.fireFlame > 0) parts.push(`${sliderLevel(gs.fireFlame)} fire and flames`);
  if (gs.lightningOverlay) parts.push('dramatic lightning overlay');

  // Output
  if (gs.renderQuality && gs.renderQuality !== '4k') parts.push(`${gs.renderQuality.toUpperCase()} render quality`);
  if (gs.noiseReduction > 50) parts.push('clean noise-free output');
  if (gs.upscaleMode && gs.upscaleMode !== 'none') parts.push(`${gs.upscaleMode.replace(/_/g, ' ')} upscaling`);
  if (gs.exportProfile && gs.exportProfile !== 'web') parts.push(`optimized for ${gs.exportProfile.replace(/_/g, ' ')}`);

  return parts;
}

const RATIO_DESC: Record<AspectRatio, string> = {
  '1:1': 'Square format (1:1)', '4:5': 'Instagram portrait (4:5)', '5:4': 'Landscape (5:4)',
  '3:4': 'Classic portrait (3:4)', '4:3': 'Classic TV (4:3)', '2:3': 'Full portrait (2:3)',
  '3:2': 'Classic photo (3:2)', '9:16': 'Vertical story (9:16)', '16:9': 'Widescreen format (16:9)',
  '21:9': 'Ultra-wide format (21:9)', '2.39:1': 'Anamorphic format (2.39:1)',
  '1.85:1': 'Film standard (1.85:1)', '5:7': 'Medium portrait (5:7)', '7:5': 'Wide photo (7:5)',
  '1:2': 'Tall vertical (1:2)', '2:1': 'Univisium (2:1)',
};

function describeDynamics(dynamics: SceneDynamics): string[] {
  const parts: string[] = [];
  parts.push(dynamics.horizontalFraming);
  parts.push(dynamics.verticalDominance);
  if (dynamics.cameraCharacterDistanceDesc) parts.push(dynamics.cameraCharacterDistanceDesc);
  if (dynamics.lightCharacterDistanceDesc) parts.push(dynamics.lightCharacterDistanceDesc);
  dynamics.spatialRelationships.forEach(r => parts.push(r));
  return parts;
}

export interface PromptResult {
  text: string;
  conflicts: Conflict[];
  inferred: InferredLayer[];
  locked: boolean;
  vfxLayers: VFXLayer[];
  vfxIntensity: VFXIntensity;
  styleTendencies: StyleTendency[];
  /**
   * Optional deterministic quality score for the generated prompt.
   * Presentational layers can surface this as a badge without
   * needing to re-run any analysis.
   */
  score?: number;
}

export function generatePrompt(
  elements: CanvasElement[],
  aspectRatio: AspectRatio,
  globalSettings: GlobalSettings,
  sceneDescription?: string,
  style?: string,
  format?: PromptFormat,
  paletteId?: string,
  customPalette?: CustomPalette | null,
): PromptResult {
  if (elements.length === 0 && !sceneDescription) {
    return { text: 'Place elements on the canvas to generate a prompt...', conflicts: [], inferred: [], locked: false, vfxLayers: [], vfxIntensity: 'low', styleTendencies: [] };
  }

  // new: deterministic validation layer before generation
  const validation = validatePrompt({ elements, globalSettings, style: style || '' });
  if (validation.status === 'conflict') {
    const validationConflicts: Conflict[] = validation.conflicts.map((msg, idx) => ({
      id: `validation_${idx}`,
      severity: 'error',
      message: msg,
    }));
    return {
      text: 'Prompt generation blocked due to conflicting configuration.',
      conflicts: validationConflicts,
      inferred: [],
      locked: true,
      vfxLayers: [],
      vfxIntensity: 'low',
      styleTendencies: [],
    };
  }

  // collect any warning-level validation results to inject later
  const initialConflicts: Conflict[] = [];
  if (validation.status === 'warning') {
    validation.warnings.forEach((msg, idx) => {
      initialConflicts.push({ id: `validation_warn_${idx}`, severity: 'warning', message: msg });
    });
  }

  // 1) Conflict detection & auto-resolution
  const conflicts = detectConflicts(elements, globalSettings, sceneDescription);
  // merge with validation hints (warnings)
  const mergedConflicts = [...initialConflicts, ...conflicts];
  const resolved = resolveConflicts(elements, globalSettings, mergedConflicts);
  // keep final conflict list for output and locking
  const finalConflicts = mergedConflicts;

  // 2) Auto-structure: infer missing cinematic layers
  const inferred = inferCinematicLayers(resolved.elements, resolved.globalSettings, sceneDescription);

  // 3) VFX Layer Engine: build structured layers
  const gs = resolved.globalSettings;
  const vfxLayers = buildVFXLayers(gs);
  const vfxIntensity = getVFXIntensity(gs);

  // 4) Style Memory: update session preferences
  updateStyleMemory(gs, style || 'none', sceneDescription || '');
  const styleTendencies = getStyleTendencies();
  const memoryEnrichments = getStyleMemoryEnrichments(gs);

  // Use resolved data
  const els = resolved.elements;
  const lights = els.filter(e => e.type === 'light');
  const cameras = els.filter(e => e.type === 'camera');
  const characters = els.filter(e => e.type === 'character');
  const dynamics = calculateSceneDynamics(els);

  const locked = isGenerationLocked(finalConflicts);

  // ── Generate ONE canonical base prompt, then reformat for all output styles ──
  const parts: string[] = [];
  parts.push(RATIO_DESC[aspectRatio]);

  if (style && style !== 'none') {
    const styleDesc = getStylePrompt(style);
    if (styleDesc) parts.push(`Style: ${styleDesc}`);
  }

  if (customPalette) {
    parts.push(`Palette: ${palettePromptFromCustom(customPalette)}`);
  } else if (paletteId && paletteId !== 'none') {
    const selectedPalette = PALETTE_PRESETS.find(p => p.id === paletteId);
    if (selectedPalette) parts.push(`Palette: ${palettePromptWithCodes(selectedPalette.keywords, selectedPalette.colors)}`);
  }

  if (sceneDescription?.trim()) {
    const coveredGroups = getCoveredTokenGroups(els);
    const cleanedScene = coveredGroups.length > 0
      ? sanitizeSceneDescription(sceneDescription.trim(), coveredGroups)
      : sceneDescription.trim();
    if (cleanedScene) parts.push(`Scene: ${cleanedScene}`);
  }

  const dynamicParts = describeDynamics(dynamics);
  if (dynamicParts.length > 0) parts.push(dynamicParts.join(', '));

  characters.forEach(c => parts.push(describeCharacter(c)));
  lights.forEach((l, i) => {
    const prefix = lights.length > 1 ? `Light ${i + 1}: ` : '';
    parts.push(prefix + describeLighting(l));
  });
  cameras.forEach((c, i) => {
    const prefix = cameras.length > 1 ? `Camera ${i + 1}: ` : '';
    parts.push(prefix + describeCamera(c));
  });

  const globalParts = describeGlobalSettings(gs);
  if (globalParts.length > 0) parts.push(globalParts.join(', '));

  const vfxParts = describeVFXLayers(vfxLayers);
  if (vfxParts.length > 0) parts.push(vfxParts.join(', '));

  const inferredParts = inferred.map(l => l.detail);
  if (inferredParts.length > 0) parts.push(inferredParts.join(', '));

  if (memoryEnrichments.length > 0) parts.push(memoryEnrichments.join(', '));

  const rawText = parts.join('. ') + '.';
  const baseText = deduplicatePrompt(rawText);

  // If a non-structured format is selected, reformat the same base content
  if (format && format !== 'structured') {
    const text = formatPrompt(baseText, format);
    return { text, conflicts: finalConflicts, inferred, locked, vfxLayers, vfxIntensity, styleTendencies };
  }

  // Default: structured (plain sentence-based)
  return { text: baseText, conflicts: finalConflicts, inferred, locked, vfxLayers, vfxIntensity, styleTendencies };
}



function generateWeighted(
  characters: CanvasElement[], lights: CanvasElement[], cameras: CanvasElement[],
  aspectRatio: AspectRatio, gs: GlobalSettings, dynamics: SceneDynamics,
  scene?: string, style?: string, inferred?: InferredLayer[], vfxLayers?: VFXLayer[], memoryEnrichments?: string[],
  paletteId?: string, customPalette?: CustomPalette | null,
): string {
  const parts: string[] = [];

  characters.forEach(c => {
    const subject = c.properties.subject || 'subject';
    const pose = c.properties.pose ? `, ${c.properties.pose.replace(/_/g, ' ')}` : '';
    const exp = c.properties.expression && c.properties.expression !== 'neutral' ? `, ${c.properties.expression} expression` : '';
    parts.push(`(${subject}${pose}${exp}:1.4)`);
  });

  if (scene?.trim()) {
    const allEls = [...characters, ...lights, ...cameras];
    const coveredGroups = getCoveredTokenGroups(allEls);
    const cleanedScene = coveredGroups.length > 0 ? sanitizeSceneDescription(scene.trim(), coveredGroups) : scene.trim();
    if (cleanedScene) parts.push(`(${cleanedScene}:1.2)`);
  }

  // Dynamic spatial context
  if (dynamics.cameraCharacterDistanceDesc) parts.push(`(${dynamics.cameraCharacterDistanceDesc}:1.1)`);
  parts.push(`(${dynamics.horizontalFraming}:1.0)`);

  lights.forEach(l => {
    const lightStyle = l.properties.lightStyle || 'natural';
    const pos = describePosition(l.x, l.y);
    const intensity = parseInt(l.properties.intensity || '50');
    const weight = intensity > 75 ? '1.3' : intensity > 50 ? '1.2' : '1.1';
    const lightDesc = getLightingPrompt(lightStyle, intensity);
    parts.push(`(${lightDesc || lightStyle.replace(/_/g, ' ') + ' lighting'} from ${pos}:${weight})`);
  });

  cameras.forEach(c => {
    const lens = c.properties.lens || '50mm';
    const shot = c.properties.shotType || 'medium';
    parts.push(`(${shot.replace(/_/g, ' ')} shot, ${lens} lens:1.1)`);
    if (c.properties.dof === 'very_shallow' || c.properties.dof === 'razor_thin') {
      parts.push('(bokeh, shallow depth of field:1.2)');
    }
  });

  if (style && style !== 'none') {
    const styleDesc = getStylePrompt(style);
    if (styleDesc) parts.push(`(${styleDesc}:1.3)`);
  }

  if (customPalette) {
    parts.push(`(${palettePromptFromCustom(customPalette)}:1.15)`);
  } else if (paletteId && paletteId !== 'none') {
    const selectedPalette = PALETTE_PRESETS.find(p => p.id === paletteId);
    if (selectedPalette) parts.push(`(${palettePromptWithCodes(selectedPalette.keywords, selectedPalette.colors)}:1.1)`);
  }

  if (gs.particles !== 'none') parts.push(`(${gs.particles.replace(/_/g, ' ')} particles:1.1)`);
  if (gs.cinematicSmoke > 30) parts.push('(cinematic smoke:1.1)');
  if (gs.energyAura) parts.push('(energy aura glow:1.2)');
  if (gs.filmGrain) parts.push('(film grain:0.9)');

  parts.push(`(${gs.renderStyle.replace(/_/g, ' ')}:1.0)`);
  parts.push(`(${RATIO_DESC[aspectRatio]}:1.0)`);

  // Append inferred layers
  if (inferred && inferred.length > 0) {
    inferred.forEach(l => parts.push(`(${l.detail}:1.0)`));
  }

  // VFX layers as weighted tokens
  if (vfxLayers && vfxLayers.length > 0) {
    const vfxDesc = describeVFXLayers(vfxLayers);
    vfxDesc.forEach(d => parts.push(`(${d}:1.1)`));
  }

  // Style memory enrichments
  if (memoryEnrichments && memoryEnrichments.length > 0) {
    memoryEnrichments.forEach(e => parts.push(`(${e}:0.9)`));
  }

  return parts.join(', ');
}

// ─── Natural (flowing sentence) format ───

function generateNatural(
  characters: CanvasElement[], lights: CanvasElement[], cameras: CanvasElement[],
  aspectRatio: AspectRatio, gs: GlobalSettings, dynamics: SceneDynamics,
  scene?: string, style?: string, inferred?: InferredLayer[], vfxLayers?: VFXLayer[], memoryEnrichments?: string[],
  paletteId?: string, customPalette?: CustomPalette | null,
): string {
  const sentences: string[] = [];

  // Scene intro
  if (scene?.trim()) {
    sentences.push(`Imagine ${scene.trim()}.`);
  }

  // Character
  characters.forEach(c => {
    const p = c.properties;
    const subject = p.subject || 'a person';
    const pose = p.pose && p.pose !== 'standing' ? `, ${p.pose.replace(/_/g, ' ')}` : '';
    const exp = p.expression && p.expression !== 'neutral' ? ` with a ${p.expression} expression` : '';
    const pos = describePosition(c.x, c.y);
    sentences.push(`A ${subject}${pose}${exp}, positioned at the ${pos} of the frame.`);
  });

  // Camera
  cameras.forEach(c => {
    const p = c.properties;
    const lens = p.lens || '50mm';
    const shot = (p.shotType || 'medium').replace(/_/g, ' ');
    const dof = p.dof && p.dof !== 'moderate' ? ` with ${p.dof.replace(/_/g, ' ')} depth of field` : '';
    sentences.push(`Shot as a ${shot} using a ${lens} lens${dof}.`);
  });

  // Lighting
  lights.forEach(l => {
    const p = l.properties;
    const lightStyle = (p.lightStyle || 'natural').replace(/_/g, ' ');
    const intensity = parseInt(p.intensity || '50');
    const intensityWord = intensity <= 30 ? 'soft' : intensity <= 60 ? 'balanced' : 'dramatic';
    const pos = describePosition(l.x, l.y);
    sentences.push(`${intensityWord.charAt(0).toUpperCase() + intensityWord.slice(1)} ${lightStyle} lighting from the ${pos}.`);
  });

  // Style
  if (style && style !== 'none') {
    const styleDesc = getStylePrompt(style);
    if (styleDesc) sentences.push(`Rendered in ${styleDesc} style.`);
  }

  // Palette
  if (customPalette) {
    sentences.push(`Using a ${palettePromptFromCustom(customPalette)} color palette.`);
  } else if (paletteId && paletteId !== 'none') {
    const selectedPalette = PALETTE_PRESETS.find(p => p.id === paletteId);
    if (selectedPalette) sentences.push(`Color palette: ${selectedPalette.keywords}.`);
  }

  // Format
  sentences.push(`Composed in ${RATIO_DESC[aspectRatio].toLowerCase()}.`);

  return sentences.join(' ');
}

// ─── JSON format ───

function generateJSON(
  characters: CanvasElement[], lights: CanvasElement[], cameras: CanvasElement[],
  aspectRatio: AspectRatio, gs: GlobalSettings, dynamics: SceneDynamics,
  scene?: string, style?: string, inferred?: InferredLayer[], vfxLayers?: VFXLayer[],
  paletteId?: string, customPalette?: CustomPalette | null,
): string {
  const data: Record<string, unknown> = {
    scene: scene?.trim() || null,
    aspect_ratio: aspectRatio,
    style: style && style !== 'none' ? style : null,
    palette: customPalette ? palettePromptFromCustom(customPalette) : paletteId && paletteId !== 'none' ? paletteId : null,
    render: {
      style: gs.renderStyle,
      resolution: gs.resolutionScale,
      hdr: gs.hdrControl > 50,
      film_grain: gs.filmGrain,
    },
    characters: characters.map(c => ({
      subject: c.properties.subject || 'subject',
      position: describePosition(c.x, c.y),
      pose: c.properties.pose || 'standing',
      expression: c.properties.expression || 'neutral',
      framing: c.properties.characterFraming || 'full',
    })),
    cameras: cameras.map(c => ({
      lens: c.properties.lens || '50mm',
      shot_type: c.properties.shotType || 'medium',
      perspective: c.properties.perspective || 'eye_level',
      dof: c.properties.dof || 'moderate',
      movement: c.properties.cameraMovement || 'static',
    })),
    lights: lights.map(l => ({
      style: l.properties.lightStyle || 'natural',
      intensity: parseInt(l.properties.intensity || '50'),
      color_temp: l.properties.colorTemp || 'neutral',
      position: describePosition(l.x, l.y),
      preset: l.properties.lightPreset || 'none',
    })),
    vfx: {
      particles: gs.particles !== 'none' ? gs.particles : null,
      fog: gs.fogHaze > 20 ? gs.fogHaze : null,
      smoke: gs.cinematicSmoke > 20 ? gs.cinematicSmoke : null,
      god_rays: gs.godRaysGlobal > 20 ? gs.godRaysGlobal : null,
    },
    composition: {
      rule_of_thirds: gs.ruleOfThirds,
      golden_ratio: gs.goldenRatio,
      centered: gs.centeredCinematic,
      symmetry: gs.symmetryMode,
    },
  };

  return JSON.stringify(data, null, 2);
}

// ─── Markdown format ───

function generateMarkdown(
  characters: CanvasElement[], lights: CanvasElement[], cameras: CanvasElement[],
  aspectRatio: AspectRatio, gs: GlobalSettings, dynamics: SceneDynamics,
  scene?: string, style?: string, inferred?: InferredLayer[], vfxLayers?: VFXLayer[],
  paletteId?: string, customPalette?: CustomPalette | null,
): string {
  const lines: string[] = [];

  lines.push('# Scene Prompt');
  lines.push('');

  if (scene?.trim()) {
    lines.push(`## Scene`);
    lines.push(scene.trim());
    lines.push('');
  }

  if (characters.length > 0) {
    lines.push('## Characters');
    characters.forEach(c => {
      const p = c.properties;
      lines.push(`- **${p.subject || 'Subject'}** — ${p.pose || 'standing'}, ${p.expression || 'neutral'} expression, ${describePosition(c.x, c.y)}`);
    });
    lines.push('');
  }

  if (cameras.length > 0) {
    lines.push('## Camera');
    cameras.forEach(c => {
      const p = c.properties;
      lines.push(`- **Lens:** ${p.lens || '50mm'}`);
      lines.push(`- **Shot:** ${(p.shotType || 'medium').replace(/_/g, ' ')}`);
      lines.push(`- **Perspective:** ${(p.perspective || 'eye_level').replace(/_/g, ' ')}`);
      lines.push(`- **DOF:** ${(p.dof || 'moderate').replace(/_/g, ' ')}`);
    });
    lines.push('');
  }

  if (lights.length > 0) {
    lines.push('## Lighting');
    lights.forEach(l => {
      const p = l.properties;
      lines.push(`- **Style:** ${(p.lightStyle || 'natural').replace(/_/g, ' ')}`);
      lines.push(`- **Intensity:** ${p.intensity || '50'}%`);
      lines.push(`- **Color Temp:** ${p.colorTemp || 'neutral'}`);
    });
    lines.push('');
  }

  lines.push('## Settings');
  lines.push(`- **Format:** ${RATIO_DESC[aspectRatio]}`);
  lines.push(`- **Render:** ${gs.renderStyle.replace(/_/g, ' ')}`);
  if (style && style !== 'none') lines.push(`- **Style:** ${style}`);
  if (paletteId && paletteId !== 'none') lines.push(`- **Palette:** ${paletteId}`);

  return lines.join('\n');
}

// ─── Tags format (comma-separated tags) ───

function generateTags(
  characters: CanvasElement[], lights: CanvasElement[], cameras: CanvasElement[],
  aspectRatio: AspectRatio, gs: GlobalSettings, dynamics: SceneDynamics,
  scene?: string, style?: string, inferred?: InferredLayer[], vfxLayers?: VFXLayer[],
  paletteId?: string, customPalette?: CustomPalette | null,
): string {
  const tags: string[] = [];

  // Scene
  if (scene?.trim()) {
    scene.trim().split(/[,.]/).forEach(s => {
      const t = s.trim();
      if (t) tags.push(t);
    });
  }

  // Character tags
  characters.forEach(c => {
    const p = c.properties;
    if (p.subject && p.subject !== 'a person') tags.push(p.subject);
    if (p.pose && p.pose !== 'standing') tags.push(p.pose.replace(/_/g, ' '));
    if (p.expression && p.expression !== 'neutral') tags.push(`${p.expression} expression`);
  });

  // Camera tags
  cameras.forEach(c => {
    const p = c.properties;
    tags.push(`${(p.shotType || 'medium').replace(/_/g, ' ')} shot`);
    tags.push(`${p.lens || '50mm'} lens`);
    if (p.dof && p.dof !== 'moderate') tags.push(`${p.dof.replace(/_/g, ' ')} dof`);
    if (p.perspective && p.perspective !== 'eye_level') tags.push(p.perspective.replace(/_/g, ' '));
  });

  // Light tags
  lights.forEach(l => {
    const p = l.properties;
    tags.push(`${(p.lightStyle || 'natural').replace(/_/g, ' ')} lighting`);
    if (p.colorTemp && p.colorTemp !== 'neutral') tags.push(`${p.colorTemp} tones`);
    if (p.lightPreset && p.lightPreset !== 'none') tags.push(p.lightPreset.replace(/_/g, ' '));
  });

  // Style
  if (style && style !== 'none') tags.push(style.replace(/_/g, ' '));
  if (paletteId && paletteId !== 'none') tags.push(paletteId.replace(/_/g, ' '));
  tags.push(gs.renderStyle.replace(/_/g, ' '));
  tags.push(aspectRatio);

  // VFX
  if (gs.particles !== 'none') tags.push(gs.particles.replace(/_/g, ' '));
  if (gs.filmGrain) tags.push('film grain');
  if (gs.cinematicSmoke > 20) tags.push('cinematic smoke');
  if (gs.godRaysGlobal > 20) tags.push('god rays');

  // Inferred
  if (inferred) {
    inferred.forEach(l => tags.push(l.detail));
  }

  return tags.join(', ');
}
